import { Menu, MenuItem, Tooltip } from '@mui/material';
import type { MouseEvent } from 'react';
import React, { useContext, useState } from 'react';
import { SECURITY_ACTIONS, SecuredIconButton } from '../auth/Security';
import { UserContext } from '../auth/UserContext';
import { COLOR_VARIANTS, SIZES } from '../constants/constants';
import I18n, { useI18n } from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { downloadCsvFile } from '../utils/commonUtils';

interface ExportMenuProps {
    tableData?: [];
    mapEntityToCsvRows?: (data: Record<string, unknown>[]) => Record<string, unknown>[];
    exportFileName?: string;
}

export default function ListViewExportMenu({ tableData, mapEntityToCsvRows, exportFileName }: ExportMenuProps) {
    const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
    const isExportMenuOpen = Boolean(exportAnchorEl);
    const exportTooltip = useI18n('common.export.tooltip');
    const { activeOrganizationAccount } = useContext(UserContext);

    return (
        <>
            <Tooltip title={exportTooltip}>
                <SecuredIconButton
                    securityAction={SECURITY_ACTIONS.READ}
                    size={SIZES.small}
                    color={COLOR_VARIANTS.primary}
                    onClick={(event: MouseEvent<HTMLElement>) => setExportAnchorEl(event.currentTarget)}
                    aria-label={exportTooltip}
                >
                    <Icons.FileDownload />
                </SecuredIconButton>
            </Tooltip>

            <Menu
                anchorEl={exportAnchorEl}
                open={isExportMenuOpen}
                onClose={() => setExportAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                sx={{ mt: 1 }}
            >
                <MenuItem
                    onClick={async () => {
                        try {
                            await downloadCsvFile(
                                tableData,
                                mapEntityToCsvRows,
                                exportFileName,
                                activeOrganizationAccount
                            );
                        } catch (error) {
                            console.error('Error exporting CSV file:', error);
                        } finally {
                            setExportAnchorEl(null);
                        }
                    }}
                >
                    <I18n token="common.downloadCSV" />
                </MenuItem>
            </Menu>
        </>
    );
}
